// Utility functions for reports

export const reportUtils = {
  /**
   * Get the first working day of the current month
   */
  getFirstWorkingDayOfMonth: () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // Create first day of current month at noon UTC to avoid timezone issues
    const firstDay = new Date(Date.UTC(year, month, 1, 12, 0, 0));
    
    // Get day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
    const dayOfWeek = firstDay.getUTCDay();
    
    // If it's Saturday (6), move to Monday (+2 days)
    // If it's Sunday (0), move to Monday (+1 day)
    // Otherwise keep as is
    if (dayOfWeek === 6) { // Saturday
      firstDay.setUTCDate(firstDay.getUTCDate() + 2);
    } else if (dayOfWeek === 0) { // Sunday
      firstDay.setUTCDate(firstDay.getUTCDate() + 1);
    }
    
    // Format as YYYY-MM-DD using UTC methods
    const year2 = firstDay.getUTCFullYear();
    const month2 = String(firstDay.getUTCMonth() + 1).padStart(2, '0');
    const day = String(firstDay.getUTCDate()).padStart(2, '0');
    const firstWorkingDay = `${year2}-${month2}-${day}`;
    
    // Never start after today (e.g. on a weekend 1st of the month)
    const today = reportUtils.getTodayDate();
    return firstWorkingDay > today ? `${year}-${String(month + 1).padStart(2, '0')}-01` : firstWorkingDay;
  },

  /**
   * Generate a unique report ID
   */
  generateReportId: () => {
    return `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Get current report state for comparison
   */
  getCurrentReportState: (reportData, reportSections, selectedPeriod, selectedSite, selectedPhotos, photoLabels, importedPhotos) => {
    // Comprehensive state capture for ALL report sections and their nested data
    return {
      // Basic report metadata
      reportData: {
        title: reportData.title || '',
        description: reportData.description || '',
        author: reportData.author || '',
        signature: reportData.signature || ''
      },
      
      // Selection data
      selectedPeriod: selectedPeriod || { startDate: '', endDate: '' },
      selectedSite: selectedSite || '',
      
      // Photo data
      selectedPhotos: selectedPhotos || [],
      photoLabels: photoLabels || {},
      importedPhotos: importedPhotos || [],
      
      // ALL Report sections with FULL nested data structure
      reportSections: {
        reportOptions: {
          excludedSections: reportSections.reportOptions?.excludedSections || []
        },
        // Cover Page
        coverPage: {
          client: reportSections.coverPage?.client || '',
          contractor: reportSections.coverPage?.contractor || '',
          reportAuthor: reportSections.coverPage?.reportAuthor || '',
          reportDate: reportSections.coverPage?.reportDate || ''
        },
        
        // Section 2.0 - Executive Summary (with userTextboxes and photos)
        executiveSummary: {
          projectOverview: reportSections.executiveSummary?.projectOverview || '',
          keyMetrics: reportSections.executiveSummary?.keyMetrics || '',
          majorAchievements: reportSections.executiveSummary?.majorAchievements || '',
          challengesIssues: reportSections.executiveSummary?.challengesIssues || '',
          nextSteps: reportSections.executiveSummary?.nextSteps || '',
          userTextboxes: reportSections.executiveSummary?.userTextboxes || [],
          textboxPhotos: reportSections.executiveSummary?.textboxPhotos || {}
        },
        
        // Section 3.0 - Project Overview (with userTextboxes and photos)
        projectOverview: {
          projectDescription: reportSections.projectOverview?.projectDescription || '',
          scope: reportSections.projectOverview?.scope || '',
          objectives: reportSections.projectOverview?.objectives || '',
          timeline: reportSections.projectOverview?.timeline || '',
          stakeholders: reportSections.projectOverview?.stakeholders || '',
          constraints: reportSections.projectOverview?.constraints || '',
          qualityStandards: reportSections.projectOverview?.qualityStandards || '',
          userTextboxes: reportSections.projectOverview?.userTextboxes || [],
          textboxPhotos: reportSections.projectOverview?.textboxPhotos || {}
        },
        
        // Section 4.0 - Progress Highlights (with sliders and userTextboxes)
        progressHighlights: {
          majorMilestones: reportSections.progressHighlights?.majorMilestones || '',
          notableAchievements: reportSections.progressHighlights?.notableAchievements || '',
          performanceImprovements: reportSections.progressHighlights?.performanceImprovements || '',
          teamRecognition: reportSections.progressHighlights?.teamRecognition || '',
          overallCompletion: reportSections.progressHighlights?.overallCompletion || 0,
          schedulePerformance: reportSections.progressHighlights?.schedulePerformance || 0,
          budgetPerformance: reportSections.progressHighlights?.budgetPerformance || 0,
          qualityScore: reportSections.progressHighlights?.qualityScore || 0,
          safetyScore: reportSections.progressHighlights?.safetyScore || 0,
          userTextboxes: reportSections.progressHighlights?.userTextboxes || [],
          textboxPhotos: reportSections.progressHighlights?.textboxPhotos || {}
        },
        
        // Section 5.0 - Work Completed (with userTextboxes and photos)
        workCompleted: {
          structuralWork: reportSections.workCompleted?.structuralWork || '',
          mepSystems: reportSections.workCompleted?.mepSystems || '',
          architecturalWork: reportSections.workCompleted?.architecturalWork || '',
          siteWork: reportSections.workCompleted?.siteWork || '',
          testingCommissioning: reportSections.workCompleted?.testingCommissioning || '',
          userTextboxes: reportSections.workCompleted?.userTextboxes || [],
          textboxPhotos: reportSections.workCompleted?.textboxPhotos || {}
        },
        
        // Section 6.0 - Quality Inspections (with metrics, userTextboxes and photos)
        qualityInspections: {
          overview: reportSections.qualityInspections?.overview || '',
          inspectionDetails: reportSections.qualityInspections?.inspectionDetails || '',
          standardsCompliance: reportSections.qualityInspections?.standardsCompliance || '',
          nonConformances: reportSections.qualityInspections?.nonConformances || '',
          materialTesting: reportSections.qualityInspections?.materialTesting || '',
          qualityImprovements: reportSections.qualityInspections?.qualityImprovements || '',
          totalInspections: reportSections.qualityInspections?.totalInspections || '',
          inspectionsPassed: reportSections.qualityInspections?.inspectionsPassed || '',
          inspectionsFailed: reportSections.qualityInspections?.inspectionsFailed || '',
          successRate: reportSections.qualityInspections?.successRate || '',
          userTextboxes: reportSections.qualityInspections?.userTextboxes || [],
          textboxPhotos: reportSections.qualityInspections?.textboxPhotos || {}
        },
        
        // Section 7.0 - Safety Compliance (with metrics, userTextboxes and photos)
        safetyCompliance: {
          incidentReports: reportSections.safetyCompliance?.incidentReports || '',
          complianceStatus: reportSections.safetyCompliance?.complianceStatus || '',
          safetyInspections: reportSections.safetyCompliance?.safetyInspections || '',
          ppeCompliance: reportSections.safetyCompliance?.ppeCompliance || '',
          safetyTraining: reportSections.safetyCompliance?.safetyTraining || '',
          safetyImprovements: reportSections.safetyCompliance?.safetyImprovements || '',
          daysWithoutIncident: reportSections.safetyCompliance?.daysWithoutIncident || '',
          totalLaborHours: reportSections.safetyCompliance?.totalLaborHours || '',
          nearMisses: reportSections.safetyCompliance?.nearMisses || '',
          trainingHours: reportSections.safetyCompliance?.trainingHours || '',
          userTextboxes: reportSections.safetyCompliance?.userTextboxes || [],
          textboxPhotos: reportSections.safetyCompliance?.textboxPhotos || {}
        },
        
        // Section 8.0 - Schedule Performance (with metrics, userTextboxes and photos)
        schedulePerformance: {
          criticalPath: reportSections.schedulePerformance?.criticalPath || '',
          scheduleVariances: reportSections.schedulePerformance?.scheduleVariances || '',
          milestonePerformance: reportSections.schedulePerformance?.milestonePerformance || '',
          resourceImpact: reportSections.schedulePerformance?.resourceImpact || '',
          recoveryStrategies: reportSections.schedulePerformance?.recoveryStrategies || '',
          cashflowImpact: reportSections.schedulePerformance?.cashflowImpact || '',
          originalDuration: reportSections.schedulePerformance?.originalDuration || '',
          revisedDuration: reportSections.schedulePerformance?.revisedDuration || '',
          spi: reportSections.schedulePerformance?.spi || '',
          userTextboxes: reportSections.schedulePerformance?.userTextboxes || [],
          textboxPhotos: reportSections.schedulePerformance?.textboxPhotos || {}
        },
        
        // Section 9.0 - Resource Utilization (with 5 dynamic tables, userTextboxes and photos)
        resourceUtilization: {
          laborAnalysis: reportSections.resourceUtilization?.laborAnalysis || '',
          equipmentAnalysis: reportSections.resourceUtilization?.equipmentAnalysis || '',
          materialManagement: reportSections.resourceUtilization?.materialManagement || '',
          optimizationStrategies: reportSections.resourceUtilization?.optimizationStrategies || '',
          performanceBenchmarks: reportSections.resourceUtilization?.performanceBenchmarks || '',
          labour: reportSections.resourceUtilization?.labour || [],
          materialsOnSite: reportSections.resourceUtilization?.materialsOnSite || [],
          plantOnSite: reportSections.resourceUtilization?.plantOnSite || [],
          materialsNeeded: reportSections.resourceUtilization?.materialsNeeded || [],
          plantNeeded: reportSections.resourceUtilization?.plantNeeded || [],
          userTextboxes: reportSections.resourceUtilization?.userTextboxes || [],
          textboxPhotos: reportSections.resourceUtilization?.textboxPhotos || {}
        },
        
        // Section 10.0 - Budget Analysis (with metrics, userTextboxes and photos)
        budgetAnalysis: {
          varianceAnalysis: reportSections.budgetAnalysis?.varianceAnalysis || '',
          changeOrders: reportSections.budgetAnalysis?.changeOrders || '',
          cashFlow: reportSections.budgetAnalysis?.cashFlow || '',
          costForecast: reportSections.budgetAnalysis?.costForecast || '',
          originalBudget: reportSections.budgetAnalysis?.originalBudget || '',
          revisedBudget: reportSections.budgetAnalysis?.revisedBudget || '',
          laborCosts: reportSections.budgetAnalysis?.laborCosts || '',
          materialCosts: reportSections.budgetAnalysis?.materialCosts || '',
          equipmentCosts: reportSections.budgetAnalysis?.equipmentCosts || '',
          overheadCosts: reportSections.budgetAnalysis?.overheadCosts || '',
          userTextboxes: reportSections.budgetAnalysis?.userTextboxes || [],
          textboxPhotos: reportSections.budgetAnalysis?.textboxPhotos || {}
        },
        
        // Section 11.0 - Risk Assessment (with metrics, userTextboxes and photos)
        riskAssessment: {
          technicalDesignRisks: reportSections.riskAssessment?.technicalDesignRisks || '',
          financialCommercialRisks: reportSections.riskAssessment?.financialCommercialRisks || '',
          environmentalExternalRisks: reportSections.riskAssessment?.environmentalExternalRisks || '',
          operationalResourceRisks: reportSections.riskAssessment?.operationalResourceRisks || '',
          otherRisks: reportSections.riskAssessment?.otherRisks || '',
          highPriorityAnalysis: reportSections.riskAssessment?.highPriorityAnalysis || '',
          mitigationStrategies: reportSections.riskAssessment?.mitigationStrategies || '',
          contingencyPlans: reportSections.riskAssessment?.contingencyPlans || '',
          riskMonitoring: reportSections.riskAssessment?.riskMonitoring || '',
          totalRisks: reportSections.riskAssessment?.totalRisks || '',
          highPriorityRisks: reportSections.riskAssessment?.highPriorityRisks || '',
          risksMitigated: reportSections.riskAssessment?.risksMitigated || '',
          residualRiskIndex: reportSections.riskAssessment?.residualRiskIndex || '',
          technicalRisks: reportSections.riskAssessment?.technicalRisks || '',
          financialRisks: reportSections.riskAssessment?.financialRisks || '',
          environmentalRisks: reportSections.riskAssessment?.environmentalRisks || '',
          operationalRisks: reportSections.riskAssessment?.operationalRisks || '',
          otherRisksCount: reportSections.riskAssessment?.otherRisksCount || '',
          userTextboxes: reportSections.riskAssessment?.userTextboxes || [],
          textboxPhotos: reportSections.riskAssessment?.textboxPhotos || {}
        },
        
        // Section 12.0 - Progress Comparisons (with photo grids)
        progressComparisons: {
          photoGrids: reportSections.progressComparisons?.photoGrids || []
        },
        
        // Section 13.0 - Technical Drawings (with 4 tables + custom tables, metrics)
        technicalDrawings: {
          totalDrawings: reportSections.technicalDrawings?.totalDrawings || '',
          revisions: reportSections.technicalDrawings?.revisions || '',
          completedDrawings: reportSections.technicalDrawings?.completedDrawings || '',
          architectural: reportSections.technicalDrawings?.architectural || [],
          structural: reportSections.technicalDrawings?.structural || [],
          mep: reportSections.technicalDrawings?.mep || [],
          other: reportSections.technicalDrawings?.other || [],
          customTables: reportSections.technicalDrawings?.customTables || []
        },
        
        // Section 14.0 - Next Phase (with metrics, userTextboxes and photos)
        nextPhase: {
          scopeOfWork: reportSections.nextPhase?.scopeOfWork || '',
          keyMilestones: reportSections.nextPhase?.keyMilestones || '',
          riskManagement: reportSections.nextPhase?.riskManagement || '',
          qualitySafety: reportSections.nextPhase?.qualitySafety || '',
          financialDetails: reportSections.nextPhase?.financialDetails || '',
          coordination: reportSections.nextPhase?.coordination || '',
          phaseName: reportSections.nextPhase?.phaseName || '',
          startDate: reportSections.nextPhase?.startDate || '',
          duration: reportSections.nextPhase?.duration || '',
          userTextboxes: reportSections.nextPhase?.userTextboxes || [],
          textboxPhotos: reportSections.nextPhase?.textboxPhotos || {}
        },
        
        // Section 15.0 - Appendices (placeholder)
        appendices: {
          content: reportSections.appendices?.content || ''
        }
      }
    };
  },

  /**
   * Check if required fields are filled for report generation
   */
  checkRequiredFields: (reportData, selectedPeriod, selectedSite) => {
    return reportData.title && selectedPeriod.startDate && selectedPeriod.endDate && selectedSite;
  },

  /**
   * Track changes by comparing current state with saved state
   */
  trackChanges: (savedReportState, getCurrentReportStateFn) => {
    if (!savedReportState) {
      // If no saved state, check if there's any data entered
      const currentState = getCurrentReportStateFn();
      const hasAnyData = (
        currentState.reportData.title ||
        currentState.reportSections.executiveSummary.overview ||
        currentState.selectedPeriod.startDate ||
        currentState.selectedPeriod.endDate ||
        currentState.selectedSite ||
        (currentState.selectedPhotos && currentState.selectedPhotos.length > 0) ||
        (currentState.reportSections ? Object.values(currentState.reportSections) : []).some(section => 
          (section ? Object.values(section) : []).some(field => typeof field === 'string' && field.trim())
        )
      );
      return hasAnyData;
    } else {
      // Compare with saved state
      const currentState = getCurrentReportStateFn();
      const hasChanges = JSON.stringify(currentState) !== JSON.stringify(savedReportState);
      return hasChanges;
    }
  },

  /**
   * Get today's date in YYYY-MM-DD format
   */
  getTodayDate: () => {
    return new Date().toISOString().split('T')[0];
  },

  /**
   * Format date for display
   */
  formatDate: (dateString, format = 'locale') => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    switch (format) {
      case 'locale':
        return date.toLocaleDateString();
      case 'long':
        return date.toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: 'long', 
          year: 'numeric' 
        });
      case 'short':
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric'
        });
      default:
        return dateString;
    }
  },

  /**
   * Calculate date range duration in days
   */
  getDateRangeDuration: (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays + 1; // Include both start and end days
  }
};